import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type CapturedBlock = {
  pulseName: string;
  pulseDisplayName?: string | null;
  blockName: string;
  blockDisplayName: string;
};

type PulseBlockCaptureContextValue = {
  captures: CapturedBlock[];
  addCapture: (capture: CapturedBlock) => void;
  projectName?: string;
};

const EMPTY_CAPTURES: CapturedBlock[] = [];

const PulseBlockCaptureContext =
  createContext<PulseBlockCaptureContextValue | null>(null);

export const PulseBlockCaptureProvider = ({
  children,
  projectName,
}: {
  children: ReactNode;
  projectName?: string;
}) => {
  const [captures, setCaptures] = useState<CapturedBlock[]>([]);

  const addCapture = useCallback((capture: CapturedBlock) => {
    setCaptures(current => {
      if (current.some(c => c.blockName === capture.blockName)) return current;
      return [...current, capture];
    });
  }, []);

  const value = useMemo<PulseBlockCaptureContextValue>(
    () => ({ captures, addCapture, projectName }),
    [captures, addCapture, projectName],
  );

  return (
    <PulseBlockCaptureContext.Provider value={value}>
      {children}
    </PulseBlockCaptureContext.Provider>
  );
};

export const usePulseBlockCaptures = (): PulseBlockCaptureContextValue => {
  const value = useContext(PulseBlockCaptureContext);

  return useMemo(
    () =>
      value ?? {
        captures: EMPTY_CAPTURES,
        addCapture: () => {},
      },
    [value],
  );
};
